import { createSelector } from '@reduxjs/toolkit';
import { parse, isBefore } from 'date-fns';
import formatNumber from '../../utilities/formatNumber';
import { selectCurrentLocation } from '../map/map.selector';
import {
  selectAttributeInfo,
  selectHazardMetric,
} from '../options/options.selector';
import { getStateFips, getStateAbbr } from '../constants/locationInfo';

const selectEpisodes = (state) => state.episodes;

export const selectEpisodesData = createSelector(
  selectEpisodes,
  selectHazardMetric,
  selectCurrentLocation,
  (episodesState, metric, loc) => {
    if (!loc.id) return [];
    const isStateSelected = loc.state === null;
    const locMatcher = isStateSelected ? getStateFips(loc.id) : loc.id;
    const fipsMatchesSelectedLoc = isStateSelected ?
      (fips) => fips.slice(0,2) === locMatcher :
      (fips) => fips === locMatcher;

    const sortable = Object.values(episodesState.episodesData).reduce((acc,episodeObj) => {
      const hasEventInLoc = episodeObj.events.some(eventObj => eventObj.counties.some(fips => fipsMatchesSelectedLoc(fips)));
      const hasImpact = metric === 'count' || episodeObj.events.some(eventObj => eventObj[metric] > 0);
      if (hasEventInLoc && hasImpact) {
        const episodeCopy = JSON.parse(JSON.stringify(episodeObj));
        episodeCopy.beginDate = parse(episodeObj.beginDate, 'yyyy-MM-dd', new Date());
        episodeCopy.endDate = parse(episodeObj.endDate, 'yyyy-MM-dd', new Date());
        episodeCopy.damageCrops *= 1000;
        episodeCopy.damageProperty *= 1000;
        acc.push(episodeCopy);
      }
      return acc;
    }, []);

    return sortable.sort((a, b) =>
      isBefore(a.beginDate, b.beginDate) ? -1 : 1
    );
  }
);

export const selectYear = createSelector(
  selectEpisodes,
  (episodesState) => episodesState.year
);

export const selectIsLoadingEpisodes = createSelector(
  selectEpisodes,
  (episodesState) => episodesState.loading
);

export const selectCountyNames = createSelector(
  selectEpisodes,
  (episodesState) => episodesState.countyNames
);

export const selectEvents = createSelector(
  selectEpisodes,
  selectCountyNames,
  selectAttributeInfo,
  selectHazardMetric,
  (episodesState, countyNames, attrInfo, metric) => {
    let location = null;
    const totals = {
      crop: 0,
      prop: 0,
      death: 0,
      injury: 0,
    };

    const events = episodesState.selectedEpisodeEvents.events.reduce(
      (acc, event) => {
        if (!location) {
          if (event.counties.length > 1) {
            location = `${getStateAbbr(event.counties[0].slice(0,2))} Zone - ${event.affectedZoneName.slice(0,1)}${event.affectedZoneName.slice(1).toLowerCase()}`;
          } else {
            location = `${countyNames[event.counties[0]]}, ${getStateAbbr(event.counties[0].slice(0,2))}`;
          }
        }

        const baseObj = {
          id: event.event,
          narrative: event.narrative
            ? event.narrative
            : 'No narrative is available for this event.',
        };

        let addOns = null;

        try {
          if (metric === 'count') {
            addOns = {
              crop: formatNumber(event.damageCrops),
              prop: formatNumber(event.damageProperty),
              death: formatNumber(event.deaths),
              injury: formatNumber(event.injuries),
            };
          } else if (metric === 'damageCrops' && event.damageCrops) {
            addOns = {
              crop: formatNumber(event.damageCrops),
            };
          } else if (metric === 'damageProperty' && event.damageProperty) {
            addOns = {
              prop: formatNumber(event.damageProperty),
            };
          } else if (metric === 'deaths' && event.deaths) {
            addOns = {
              death: formatNumber(event.deaths),
            };
          } else if (metric === 'injuries' && event.injuries) {
            addOns = {
              injury: formatNumber(event.injuries),
            };
          }
        } catch {
          return acc;
        }

        if (addOns) {
          totals.crop += event.damageCrops;
          totals.prop += event.damageProperty;
          totals.death += event.deaths;
          totals.injury += event.injuries;

          acc.push({ ...baseObj, ...addOns });
        }

        return acc;
      },
      []
    );

    return {
      events,
      date: episodesState.selectedEpisodeEvents.date,
      attrName: attrInfo.name,
      location,
      totals: {
        crop: formatNumber(totals.crop),
        prop: formatNumber(totals.prop),
        death: formatNumber(totals.death),
        injury: formatNumber(totals.injury),
      },
    };
  }
);
